// Confetti animation utilities

interface ConfettiOptions {
  particleCount?: number;
  spread?: number;
  startVelocity?: number;
  decay?: number;
  gravity?: number;
  drift?: number;
  ticks?: number;
  origin?: { x: number; y: number };
  colors?: string[];
  shapes?: string[];
  scalar?: number;
  zIndex?: number;
}

// Active confetti intervals for cleanup
const activeIntervals = new Set<NodeJS.Timeout>();

/**
 * Clean up all active confetti animations
 */
export const cleanupConfetti = (): void => {
  activeIntervals.forEach(interval => clearInterval(interval));
  activeIntervals.clear();
};

/**
 * Trigger confetti animation for game wins with enhanced effects
 * @param duration Duration of the confetti animation in milliseconds
 * @param colors Custom colors for the confetti
 */
export const triggerWinConfetti = (duration: number = 3000, colors?: string[]): Promise<void> => {
  return new Promise((resolve) => {
    // Check if confetti library is available
    if (typeof window === 'undefined' || !window.confetti) {
      console.warn('Confetti library not available');
      resolve();
      return;
    }

    const animationEnd = Date.now() + duration;
    const gameColors = colors || [
      '#10b981', // Green (correct)
      '#f59e0b', // Yellow (present)  
      '#3b82f6', // Blue (theme)
      '#8b5cf6', // Purple
      '#ef4444', // Red
      '#06b6d4', // Cyan
    ];

    const defaults: ConfettiOptions = { 
      startVelocity: 30, 
      spread: 360, 
      ticks: 60, 
      zIndex: 9999,
      colors: gameColors,
      shapes: ['square', 'circle'],
      scalar: 1.2,
      gravity: 0.5,
      drift: 0,
      decay: 0.94
    };

    function randomInRange(min: number, max: number): number {
      return Math.random() * (max - min) + min;
    }

    // Initial burst
    window.confetti?.({
      ...defaults,
      particleCount: 150,
      spread: 100,
      origin: { x: 0.5, y: 0.5 },
      startVelocity: 45,
    });

    // Continuous celebration
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        activeIntervals.delete(interval);
        resolve();
        return;
      }

      const particleCount = Math.floor(30 * (timeLeft / duration));
      
      // Launch confetti from multiple positions with varying effects
      window.confetti?.({
        ...defaults,
        particleCount,
        spread: randomInRange(50, 100),
        origin: { x: randomInRange(0.1, 0.3), y: randomInRange(0.5, 0.8) },
        startVelocity: randomInRange(20, 40),
      });
      
      window.confetti?.({
        ...defaults,
        particleCount,
        spread: randomInRange(50, 100),
        origin: { x: randomInRange(0.7, 0.9), y: randomInRange(0.5, 0.8) },
        startVelocity: randomInRange(20, 40),
      });

      // Occasional center burst
      if (Math.random() < 0.3) {
        window.confetti?.({
          ...defaults,
          particleCount: Math.floor(particleCount * 1.5),
          spread: 70,
          origin: { x: 0.5, y: 0.6 },
          startVelocity: 35,
        });
      }
    }, 200);

    activeIntervals.add(interval);
  });
};

/**
 * Trigger a burst of confetti from the center
 * @param intensity Intensity of the burst (1-3)
 */
export const triggerCenterConfetti = (intensity: number = 1): void => {
  if (typeof window === 'undefined' || !window.confetti) {
    console.warn('Confetti library not available');
    return;
  }

  const particleCount = Math.floor(100 * intensity);
  const spread = Math.min(70 + (intensity * 20), 120);

  window.confetti?.({
    particleCount,
    spread,
    origin: { x: 0.5, y: 0.6 },
    colors: ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6'],
    shapes: ['square', 'circle'],
    scalar: 1 + (intensity * 0.2),
    gravity: 0.6,
    decay: 0.9,
    startVelocity: 25 + (intensity * 10),
  });
};

/**
 * Trigger confetti with custom colors and enhanced effects
 * @param colors Array of color strings
 * @param options Additional confetti options
 */
export const triggerColoredConfetti = (
  colors: string[], 
  options: Partial<ConfettiOptions> = {}
): void => {
  if (typeof window === 'undefined' || !window.confetti) {
    console.warn('Confetti library not available');
    return;
  }

  const defaults: ConfettiOptions = {
    particleCount: 100,
    spread: 70,
    origin: { x: 0.5, y: 0.6 },
    colors,
    shapes: ['square', 'circle'],
    scalar: 1.1,
    gravity: 0.6,
    decay: 0.9,
    startVelocity: 30,
    ...options
  };

  window.confetti?.(defaults);
};

/**
 * Trigger a firework-style confetti burst
 * @param position Position to launch from (x, y between 0-1)
 */
export const triggerFireworkConfetti = (position: { x: number; y: number } = { x: 0.5, y: 0.5 }): void => {
  if (typeof window === 'undefined' || !window.confetti) {
    console.warn('Confetti library not available');
    return;
  }

  // Multiple bursts with different timings
  const colors = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];
  
  // Initial burst
  window.confetti?.({
    particleCount: 80,
    spread: 360,
    origin: position,
    colors,
    shapes: ['circle'],
    scalar: 0.8,
    gravity: 0.4,
    decay: 0.95,
    startVelocity: 40,
    ticks: 100,
  });

  // Delayed secondary burst
  setTimeout(() => {
    window.confetti?.({
      particleCount: 60,
      spread: 180,
      origin: position,
      colors,
      shapes: ['square'],
      scalar: 1.2,
      gravity: 0.6,
      decay: 0.9,
      startVelocity: 25,
      ticks: 80,
    });
  }, 200);
};

/**
 * Trigger streak celebration confetti
 * @param streakCount Number of wins in streak
 */
export const triggerStreakConfetti = (streakCount: number): void => {
  if (streakCount <= 1) {
    triggerCenterConfetti(1);
    return;
  }

  // More intense celebration for longer streaks
  // const intensity = Math.min(streakCount / 5, 3);
  const duration = Math.min(2000 + (streakCount * 200), 5000);
  
  triggerWinConfetti(duration, [
    '#ffd700', // Gold
    '#ff6b35', // Orange
    '#f7931e', // Amber
    '#10b981', // Green
    '#3b82f6', // Blue
  ]);
};

// Extend the Window interface to include confetti
declare global {
  interface Window {
    confetti?: (options: ConfettiOptions) => void;
  }
}

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanupConfetti);
}

const confettiUtils = {
  triggerWinConfetti,
  triggerCenterConfetti,
  triggerColoredConfetti,
  triggerFireworkConfetti,
  triggerStreakConfetti,
  cleanupConfetti,
};

export default confettiUtils;